import React from 'react';
import { Field, useFormikContext } from 'formik';
import { FormControl, FormHelperText, TextField, Typography } from '@mui/material';
import { YesNoSelector } from '@fnol/library/src/components';
import { maxCharacterLimit } from '@fnol/library/src/lib';
import { legalPersonType, personType, translations, questionMaxLength, question255MaxLength } from '../../lib/consts';
import { IndividualCleanup } from '../Individual/Individual';
import { LegalLeasingCleanup } from '../LegalLeasingEntity/LegalLeasingEntity';
import Individual from '../Individual';
import LegalLeasingEntity from '../LegalLeasingEntity';
import LegalIndividualSelector from '../LegalIndividualSelector';

const DamageObject = () => {
    const { values, errors, touched, setFieldValue } = useFormikContext<any>();

    const type = 'objectType';
    const description = 'objectDescription';
    const year = 'objectConstructionYear';
    const location = 'objectLocation';
    const damageDescription = 'objectDamageDescription';
    const moreDamages = 'moreObjectDamagesDescription';

    const onOwnerSubmitterChange = () => {
        setFieldValue('ownerBuildingType', '');
        LegalLeasingCleanup(personType.ownerBuilding, setFieldValue);
        IndividualCleanup(personType.ownerBuilding, setFieldValue);
    };

    const handleOnInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.target.value = parseInt(e.target.value).toString().slice(0, 4);
    };

    const numberInputOnWheelPreventChange = (e: React.WheelEvent<HTMLElement>) => {
        (e.target as HTMLElement).blur();
        e.stopPropagation();
        setTimeout(() => {
            (e.target as HTMLElement).focus();
        }, 0);
    };

    return (
        <>
            <FormControl>
                <Field
                    name={type}
                    label={translations.object.TYPE}
                    as={TextField}
                    inputProps={{
                        maxLength: questionMaxLength,
                    }}
                    error={!!(touched[type] && errors[type])}
                    required
                />
                {touched[type] && errors[type] ? (
                    <FormHelperText error={true}>
                        <>{errors[type]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>
            <FormControl>
                <Field
                    name={description}
                    label={translations.object.DESCRIPTION}
                    multiline={true}
                    as={TextField}
                    inputProps={{
                        maxLength: maxCharacterLimit,
                    }}
                    maxRows={5}
                    minRows={5}
                    error={!!(touched[description] && errors[description])}
                    required
                />
                {touched[description] && errors[description] ? (
                    <FormHelperText error={true}>
                        <>{errors[description]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>
            <FormControl>
                <Field
                    name={year}
                    label={translations.object.CONSTRUCTION_YEAR}
                    inputProps={{ min: 0 }}
                    type="number"
                    onInput={handleOnInput}
                    as={TextField}
                    error={!!(touched[year] && errors[year])}
                    onWheel={numberInputOnWheelPreventChange}
                    required
                />
                {touched[year] && errors[year] ? (
                    <FormHelperText error={true}>
                        <>{errors[year]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>
            {values.submitterType === legalPersonType.individual && (
                <Typography variant="subtitle1">{translations.objectOwner.DO_YOU_OWN_OBJECT + ' *'}</Typography>
            )}
            {values.submitterType === legalPersonType.legal && (
                <Typography variant="subtitle1">
                    {translations.objectOwner.APPLICANT_OWNER_OF_OBJECT +
                        values.submitterLegalNameForDisplay.toUpperCase() +
                        translations.objectOwner.APPLICANT2 +
                        ' *'}
                </Typography>
            )}
            <FormControl>
                <YesNoSelector name="objectOwnerSubmitter" onChange={onOwnerSubmitterChange} />
            </FormControl>
            {values.objectOwnerSubmitter === 'false' && (
                <>
                    <Typography variant="subtitle1">
                        {translations.objectOwner.OWNER_LEGAL_OR_PERSONAL_ENTITY + ' *'}
                    </Typography>
                    <FormControl>
                        <LegalIndividualSelector selectorType={personType.ownerBuilding} />
                    </FormControl>

                    {values.ownerBuildingType === legalPersonType.individual && (
                        <Individual individualType={personType.ownerBuilding} />
                    )}
                    {values.ownerBuildingType === legalPersonType.legal && (
                        <LegalLeasingEntity legalEntityType={personType.ownerBuilding} />
                    )}
                </>
            )}

            <FormControl>
                <Field
                    name={location}
                    label={translations.object.LOCATION}
                    as={TextField}
                    inputProps={{
                        maxLength: question255MaxLength,
                    }}
                    error={!!(touched[location] && errors[location])}
                    required
                />
                {touched[location] && errors[location] ? (
                    <FormHelperText error={true}>
                        <>{errors[location]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={damageDescription}
                    label={translations.object.DAMAGE_DESCRIPTION}
                    multiline={true}
                    as={TextField}
                    maxRows={5}
                    minRows={5}
                    inputProps={{
                        maxLength: maxCharacterLimit,
                    }}
                    value={values[damageDescription]}
                    helperText={`${translations.REMAINING_CHARACTERS} ${
                        maxCharacterLimit - values[damageDescription].length
                    }`}
                    error={!!(touched[damageDescription] && errors[damageDescription])}
                />
                {touched[damageDescription] && errors[damageDescription] ? (
                    <FormHelperText error={true}>
                        <>{errors[damageDescription]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={moreDamages}
                    label={translations.object.MORE_DAMAGES}
                    multiline={true}
                    as={TextField}
                    maxRows={5}
                    minRows={5}
                    inputProps={{
                        maxLength: maxCharacterLimit,
                    }}
                    value={values[moreDamages]}
                    helperText={`${translations.REMAINING_CHARACTERS} ${
                        maxCharacterLimit - values[moreDamages].length
                    }`}
                />
            </FormControl>
        </>
    );
};

export default DamageObject;
